// Overview screen "path engine" — pure derivation of the setup checklist and
// the weekly per-layer action list from live signals. No fetching, no
// side-effects; every branch is deterministic given `input`. WP1 Task 3
// (`.superpowers/sdd/2026-08-16-wp1-panel-shell/task-3-brief.md`, spec §7).
//
// Honesty rule (spec §7): a null/unknown signal must NEVER be rendered as an
// invented zero or fabricated count. Every branch below that can receive
// `null` renders an honest "not measured yet" / "connect your Google
// profile" headline instead, while the action still links onward so the
// owner isn't stuck — never `action: null` on a null signal.

import { TERMS } from '@pageboss/shared';
import { locationRoutes, type Layer } from '@/lib/route-map';
import type { EffectiveTier, SiteStatus } from '@/lib/types';

export interface PathEngineInput {
  siteStatus: SiteStatus;
  tier: EffectiveTier;
  gbpState: 'locked' | 'not_connected' | 'pending_selection' | 'connected' | 'unknown';
  unrepliedReviews: number | null;
  trackingEnabled: boolean;
  trackingLastRunAt: string | null;
  aiTrackingEnabled: boolean;
  hasCompletedScan: boolean;
  openTasks: number | null;
  unreadInbox: number | null;
  now: Date;
}

export interface SetupStep {
  key: 'site' | 'gbp' | 'tracking' | 'first-report';
  label: string;
  done: boolean;
  to: string;
}

export interface LayerAction {
  layer: Layer;
  status: 'ok' | 'attention' | 'setup';
  headline: string;
  action: { label: string; to: string } | null;
}

export interface PathEngineResult {
  mode: 'setup' | 'weekly';
  steps: SetupStep[];
  stepsDone: number;
  layers: LayerAction[];
}

/** Next Monday strictly AFTER `now`, in UTC calendar days — the weekly scan
 *  rhythm runs on Monday drains (see project constants `TRACKING_PLAN`).
 *  Edge case (documented per brief): when `now` itself IS a Monday, this
 *  returns 7 days out, not `now` — the function always looks forward to the
 *  *next* scan, it never treats "today" as already-scanned-worthy. Uses
 *  UTC day-of-week so the result is independent of the runtime's local
 *  timezone (tests construct `now` as UTC midnight ISO strings). */
export function nextMonday(now: Date): Date {
  const day = now.getUTCDay(); // 0 = Sunday .. 6 = Saturday, 1 = Monday
  const daysUntil = ((1 - day + 7) % 7) || 7;
  const result = new Date(now.getTime());
  result.setUTCDate(result.getUTCDate() + daysUntil);
  return result;
}

function formatMonday(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function derivePath(businessId: string, siteId: string, input: PathEngineInput): PathEngineResult {
  const steps: SetupStep[] = [
    {
      key: 'site',
      label: 'Publish your website',
      done: input.siteStatus === 'published',
      to: locationRoutes.website(businessId, siteId),
    },
    {
      key: 'gbp',
      label: `Connect your ${TERMS.googleProfile}`,
      done: input.gbpState === 'connected',
      to: locationRoutes.googleProfile(businessId, siteId),
    },
    {
      key: 'tracking',
      label: 'Turn on tracking',
      done: input.trackingEnabled,
      to: locationRoutes.googleProfileMap(businessId, siteId),
    },
    {
      key: 'first-report',
      label: 'Get your first report',
      done: input.hasCompletedScan,
      to: locationRoutes.reports(businessId, siteId),
    },
  ];

  const stepsDone = steps.filter((s) => s.done).length;
  const mode: PathEngineResult['mode'] = stepsDone === 4 ? 'weekly' : 'setup';

  const layers: LayerAction[] = [
    foundationLayer(businessId, siteId, input),
    visibilityLayer(businessId, siteId, input),
    reputationLayer(businessId, siteId, input),
    pulseLayer(businessId, siteId, input),
  ];

  return { mode, steps, stepsDone, layers };
}

function foundationLayer(businessId: string, siteId: string, input: PathEngineInput): LayerAction {
  const to = locationRoutes.aeo(businessId, siteId);
  if (input.openTasks === null) {
    return {
      layer: 'foundation',
      status: 'setup',
      headline: 'not measured yet',
      action: { label: 'View tasks', to },
    };
  }
  if (input.openTasks > 0) {
    return {
      layer: 'foundation',
      status: 'attention',
      headline: `${input.openTasks} get-found tasks open`,
      action: { label: 'View tasks', to },
    };
  }
  return {
    layer: 'foundation',
    status: 'ok',
    headline: 'no open get-found tasks',
    action: null,
  };
}

function visibilityLayer(businessId: string, siteId: string, input: PathEngineInput): LayerAction {
  const to = locationRoutes.googleProfileMap(businessId, siteId);
  if (!input.trackingEnabled) {
    return {
      layer: 'visibility',
      status: 'setup',
      headline: 'turn on tracking to see your ranking',
      action: { label: 'Set up tracking', to },
    };
  }
  return {
    layer: 'visibility',
    status: 'ok',
    headline: `next scan ${formatMonday(nextMonday(input.now))}`,
    action: { label: 'View map', to },
  };
}

function reputationLayer(businessId: string, siteId: string, input: PathEngineInput): LayerAction {
  if (input.unrepliedReviews === null) {
    // A null count means two DIFFERENT things and they must never share one
    // headline (task-6 review, MEDIUM-1): gbpState !== 'connected' means the
    // owner genuinely hasn't connected yet, so "connect your Google profile"
    // is honest. gbpState === 'connected' means the connection exists — the
    // setup checklist's 'gbp' step (below) already reads gbpState alone and
    // marks it done — and only the REVIEW COUNT is unknown right now (a
    // transient reviews-part fetch failure, or no cached data yet). Showing
    // the connect prompt in that case would contradict the checklist in the
    // very same render. Render the honest neutral instead: connected, count
    // unknown, check back — never a fabricated 0, never a false "not
    // connected" downgrade.
    if (input.gbpState === 'connected') {
      return {
        layer: 'reputation',
        status: 'ok',
        headline: 'review data is updating — check back shortly',
        action: { label: 'View reviews', to: locationRoutes.reviews(businessId, siteId) },
      };
    }
    return {
      layer: 'reputation',
      status: 'setup',
      headline: `connect your ${TERMS.googleProfile}`,
      action: { label: `Connect ${TERMS.googleProfile}`, to: locationRoutes.googleProfile(businessId, siteId) },
    };
  }
  if (input.unrepliedReviews > 0) {
    const noun = input.unrepliedReviews === 1 ? 'review' : 'reviews';
    return {
      layer: 'reputation',
      status: 'attention',
      headline: `reply to ${input.unrepliedReviews} ${noun}`,
      action: { label: 'Reply to reviews', to: locationRoutes.reviews(businessId, siteId) },
    };
  }
  return {
    layer: 'reputation',
    status: 'ok',
    headline: 'no reviews waiting on a reply',
    action: null,
  };
}

function pulseLayer(businessId: string, siteId: string, input: PathEngineInput): LayerAction {
  const to = locationRoutes.inbox(businessId, siteId);
  if (input.unreadInbox === null) {
    return {
      layer: 'pulse',
      status: 'setup',
      headline: 'not measured yet',
      action: { label: 'Open inbox', to },
    };
  }
  if (input.unreadInbox > 0) {
    const noun = input.unreadInbox === 1 ? 'message' : 'messages';
    return {
      layer: 'pulse',
      status: 'attention',
      headline: `${input.unreadInbox} unread ${noun}`,
      action: { label: 'Open inbox', to },
    };
  }
  return {
    layer: 'pulse',
    status: 'ok',
    headline: 'inbox is all caught up',
    action: null,
  };
}
